import { equals } from 'ramda';
import React, { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import {
  FileAwarenessMonitorErrorsList,
  FileAwarenessMonitorErrorTypeTag,
  withFileUsedParam,
} from '@wix/media-manager-shared';
import { captureException } from '@sentry/react';

import { setAudioCurrentTime } from '../../dom';
import { useMusicPreview } from '../../media-manager-sdk/hooks';

export const audio = new Audio();

const AudioPlaybackContext = createContext({
  playingTrack: null,
  playTrack: (_file) => null,
  stopTrack: () => null,
});

export const AudioPlaybackProvider: FC = ({ children }) => {
  const [playingTrack, setPlayingTrack] = useState(null);
  const stopTrack = useCallback(() => setPlayingTrack(null), []);

  const playingTrackUrl = withFileUsedParam(useMusicPreview(playingTrack), {
    logger: (error) =>
      captureException(error, {
        tags: {
          [FileAwarenessMonitorErrorTypeTag]: FileAwarenessMonitorErrorsList.FailedToAppendParameter,
        },
      }),
  });

  useEffect(() => {
    if (!playingTrackUrl) {
      audio.pause();
      setAudioCurrentTime(audio, 0);
      return;
    }

    setAudioCurrentTime(audio, 0);
    audio.load();
    audio.src = playingTrackUrl;
    audio.play();
  }, [playingTrackUrl]);

  useEffect(() => {
    audio.addEventListener('ended', stopTrack, true);
    document.body.appendChild(audio);

    return () => {
      audio.removeEventListener('ended', stopTrack, true);
      audio.pause();
      document.body.removeChild(audio);
    };
  }, []);

  return (
    <AudioPlaybackContext.Provider value={{ playingTrack, playTrack: setPlayingTrack, stopTrack }}>
      {children}
    </AudioPlaybackContext.Provider>
  );
};

export const useAudioPlayback = (file = null) => {
  const state = useContext(AudioPlaybackContext);

  useEffect(
    () => () => {
      state.stopTrack();
    },
    [file],
  );

  return {
    ...state,
    isPlaying: equals(file, state.playingTrack),
  };
};
